import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ActivePacksPanel } from "@/components/coach/PackPricingMatrix";
import ThisWeekSchedule from "@/components/parent/ThisWeekSchedule";
import {
    Calendar,
    Dumbbell,
    ArrowRight,
    Shield,
    FileText,
    Loader2,
    Mail,
    Phone,
    MapPin,
    CreditCard,
    UserCircle2,
    FileSignature,
    Pencil,
    Plus,
    Tag,
} from "lucide-react";
import ProfileWizard from "@/components/profile/ProfileWizard";
import AthleteEditorModal from "@/components/profile/AthleteEditorModal";
import AthleteModeManager from "@/components/AthleteModeManager";
import LatestSessionLiteCard from "@/components/LatestSessionLiteCard";
import UpcomingSessionsCard from "@/components/UpcomingSessionsCard";
import DashboardSummaryStrip from "@/components/DashboardSummaryStrip";
import AthleteSubscriptionPanel from "@/components/dashboard/AthleteSubscriptionPanel";
import OnboardingChecklistModal from "@/components/OnboardingChecklistModal";
import ParentStatusBar from "@/components/parent/ParentStatusBar";
import MemberAlertsStrip from "@/components/MemberAlertsStrip";
import FirstStepsCard from "@/components/parent/FirstStepsCard";
import ClientDashboard from "@/pages/ClientDashboard";
import { WAIVER_TEXT, WAIVER_VERSION } from "@/lib/waiver";
import { getMissingWaivers } from "@/lib/waiverStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRACK_LABELS = {
    school_foundational: "Foundational Athleticism (8–10)",
    school_elite_youth: "Elite Youth Performance (11–14)",
    school_elite_peak: "Elite Peak Performance (14–18)",
    youth_7_10: "Foundational Athleticism (8–10)",
    youth_10_13: "Elite Youth Performance (11–14)",
    youth_14_18: "Elite Peak Performance (14–18)",
    pursuing_pro: "Pursuing Pro",
    athletic_aesthetic: "Adult Performance",
};

function formatAge(dobStr) {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
}

const ROADMAP = [
    { icon: Calendar, title: "Schedule", route: "/dashboard/schedule" },
    { icon: Dumbbell, title: "Workouts", route: "/dashboard/workouts" },
    { icon: CreditCard, title: "Billing", route: "/dashboard/billing/auto-renew" },
    { icon: Tag, title: "Pricing", route: "/dashboard/pricing" },
    { icon: FileText, title: "Assessments", route: "/dashboard/assessments" },
];

export default function Dashboard() {
    const { user, loading, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [waiverOpen, setWaiverOpen] = useState(false);
    const [cardCheckDone, setCardCheckDone] = useState(false);
    // Sachin 2026-03-05: onboarding checklist modal — surfaces waiver +
    // card requirements together as a popup on the dashboard instead of
    // full-page-redirecting the parent through /welcome/waiver and
    // /welcome/add-card. Stays open until both are done.
    const [needsCardOnFile, setNeedsCardOnFile] = useState(false);
    const [onboardingDismissed, setOnboardingDismissed] = useState(false);
    const [pricesGate, setPricesGate] = useState({ visible: true, reason: "loading" });

    // Legacy ?tab=messages → /dashboard/messages.  Several CTAs across the
    // app still link to the old query-string path (intake flow, dashboard
    // tiles, etc.).  Keep them working without an audit.
    useEffect(() => {
        if (searchParams.get("tab") === "messages") {
            const topic = searchParams.get("topic");
            navigate(topic ? `/dashboard/messages?topic=${encodeURIComponent(topic)}` : "/dashboard/messages", { replace: true });
        }
    }, [searchParams, navigate]);

    // Fetch the price-visibility gate — leads can't see prices until 15 min
    // into their assessment. Owners/coaches always pass through (visible:true).
    useEffect(() => {
        if (!user || user.role === "owner" || user.role === "coach") return;
        const API = process.env.REACT_APP_BACKEND_URL + "/api";
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/me/prices-visible`, { withCredentials: true });
                if (alive) setPricesGate(r.data || { visible: false, reason: "lead_no_assessment_yet" });
            } catch {
                // Default to visible-false on error so we don't accidentally
                // leak prices to leads when the gate fails to load.
                if (alive) setPricesGate({ visible: false, reason: "error" });
            }
        })();
        return () => { alive = false; };
    }, [user]);

    useEffect(() => {
        if (!loading && !user) navigate("/", { replace: true });
        // Staff should land on the coach console, not the client dashboard.
        if (!loading && user && (user.role === "owner" || user.role === "coach")) {
            navigate("/coach", { replace: true });
        }
        // Kids signed in via Athlete Mode get the slim, restricted home — never
        // the parent dashboard.
        if (!loading && user && user.athlete_mode_for) {
            navigate("/athlete", { replace: true });
        }
    }, [loading, user, navigate]);

    // Welcome-gate sequence — replaced 2026-03-05 (Sachin ask). Instead
    // of full-page redirecting a parent to /welcome/waiver then
    // /welcome/add-card, we detect what they still need and surface a
    // single modal on the dashboard (OnboardingChecklistModal below).
    // The /welcome/waiver and /welcome/add-card pages still exist and
    // are used as targets of the modal's action buttons — but we no
    // longer force-navigate the parent to them on load.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (loading || !user) return;
            if (user.role === "owner" || user.role === "coach" || user.athlete_mode_for) return;
            if (!user.profile_complete) return;   // wizard still runs first
            // Also don't nag right after a fresh Stripe return.
            const params = new URLSearchParams(window.location.search);
            if (params.has("card_added")) {
                if (!cancelled) { setNeedsCardOnFile(false); setCardCheckDone(true); }
                return;
            }
            try {
                const r = await axios.get(`${API}/billing/me`, { withCredentials: true });
                if (!cancelled) setNeedsCardOnFile(!r.data.has_payment_method);
            } catch (_e) {
                // Silent — never block the dashboard on a billing check failure
            } finally {
                if (!cancelled) setCardCheckDone(true);
            }
        })();
        return () => { cancelled = true; };
    }, [loading, user]);

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    // CRITICAL: never render the member/parent dashboard for staff or
    // athlete-mode kids — they're being redirected by the useEffect above,
    // but useEffect fires AFTER the first render. On slow mobile networks
    // (or even desktop occasionally) that meant a coach briefly saw
    // "WELCOME · Member Area" + ParentStatusBar before bouncing to /coach.
    // Sachin called this out specifically for BrianL@maxsc.net on 2026-02-13:
    // "It should not be showing that they are a member."
    if (user.role === "owner" || user.role === "coach" || user.athlete_mode_for) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    // Sachin 2026-03-05: no more flash-suppress on missing waiver/card
    // — the dashboard renders normally and the OnboardingChecklistModal
    // (below) is layered on top with a scrim until both are complete.

    // Pause one tick while we check Stripe for a card on file. Avoids a flash
    // of the welcome banner before the modal is ready to render.
    if (
        user.profile_complete &&
        user.role !== "owner" && user.role !== "coach" &&
        !user.athlete_mode_for &&
        !cardCheckDone &&
        !new URLSearchParams(window.location.search).has("card_added")
    ) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    const firstName =
        user.parent_first_name || (user.name || "Athlete").split(" ")[0];

    // Defensive completeness check: a user is effectively profile-complete
    // if they have the data we'd ask for in the wizard, regardless of whether
    // the `profile_complete` flag was ever flipped to true. This unblocks
    // every coach-created account (Lead Detail → +Add Athlete → invite client)
    // whose wizard flag was never set, AND prevents future drift. Source of
    // truth for the wizard remains: name + ≥1 athlete + signed waiver.
    const dataLooksComplete = Boolean(
        ((user.parent_first_name || user.name || "").trim()) &&
        (user.athletes?.length > 0 || user.athlete_count > 0) &&
        (user.waiver?.signed_at || user.waiver_signed_at)
    );
    // ANY paying member should get the real dashboard, never the wizard.
    // Sachin 2026-02-27 — "Profiles with subscriptions are still showing the
    // wrong dashboard." A user can be subscribed before signing a waiver or
    // before adding their kid (e.g. coach-onboarded via Stripe link). We
    // route them to ClientDashboard; the ProfileCompletionBanner inside
    // ClientDashboard nudges them to fill the missing pieces without
    // blocking access to schedule/billing/workouts.
    const isPayingMember = Boolean(
        user.family_has_active_sub
        || user.status === "member"
        || user.comp_access
    );
    const effectiveProfileComplete = user.profile_complete || dataLooksComplete || isPayingMember;

    // Sachin 2026-03-06 (round 3): "It pops up no matter what... start
    // over with how the waiver works." Three defense layers now:
    //   1. Backend flag `self_waiver_signed` = presence of any signed_at
    //      (no version-currency check → booking waivers count too)
    //   2. Session-scoped `onboardingDismissed` state (Close button)
    //   3. Persistent `localStorage[maxsc_onboarding_dismissed_forever]`
    //      set by the "Don't show again" button — bulletproof escape
    //      hatch if the backend flag is somehow wrong for a specific
    //      user's account.
    const dismissedForever = (() => {
        try {
            return !!window.localStorage.getItem("maxsc_onboarding_dismissed_forever");
        } catch (_e) {
            return false;
        }
    })();
    // Per-account + per-child waiver gaps (presence-based). Sachin 2026-06:
    // "parents can't find the waiver" → the popup now reappears every visit
    // while ANY waiver is unsigned (the account holder OR any athlete), and
    // can't be permanently dismissed until every waiver is signed. The
    // permanent "don't show again" opt-out only applies to the card-only case.
    const missingWaivers = getMissingWaivers(user);
    const shouldShowOnboardingPopup = (
        !user.athlete_mode_for
        && !onboardingDismissed
        && (missingWaivers.length > 0 || (needsCardOnFile && !dismissedForever))
    );
    const onboardingPopup = shouldShowOnboardingPopup ? (
        <OnboardingChecklistModal
            missingWaivers={missingWaivers}
            needsCard={needsCardOnFile}
            onDismiss={() => setOnboardingDismissed(true)}
        />
    ) : null;

    // Phase 2/3: redesigned mobile-first dashboard for parents/adult clients
    // who have completed their profile. Owner/coach/athlete-mode users are
    // already routed away above. The legacy dashboard layout below remains
    // for the wizard path (incomplete profile) until we migrate it too.
    if (
        effectiveProfileComplete
        && (user.role === "client" || !user.role)
        && !user.athlete_mode_for
    ) {
        return (
            <div data-testid="dashboard-page" className="bg-brand-obsidian min-h-screen">
                {onboardingPopup}
                <ParentStatusBar />
                <ClientDashboard user={user} />
            </div>
        );
    }

    return (
        <div data-testid="dashboard-page" className="bg-brand-obsidian min-h-screen">
            {onboardingPopup}
            <ParentStatusBar />
            <section className="relative pt-32 pb-12 md:pb-16 border-b border-zinc-900">
                <div className="absolute inset-0 bg-grid-faint opacity-30 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-5 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" />
                        Member Area · Beta
                    </div>
                    <div className="grid md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-8">
                            <h1
                                data-testid="dashboard-welcome"
                                className="font-display text-5xl md:text-7xl tracking-wider leading-[0.9] text-white"
                            >
                                WELCOME,
                                <br />
                                <span className="text-brand-red">{firstName.toUpperCase()}.</span>
                            </h1>
                            <p className="mt-5 text-zinc-200 text-base md:text-lg max-w-2xl leading-relaxed">
                                {effectiveProfileComplete
                                    ? "You're in our system. Your coach will reach out about next steps."
                                    : "Let's complete your profile and waiver so we can get you on the floor."}
                            </p>
                        </div>
                        <div className="md:col-span-4 md:text-right space-y-3">
                            {/* Status badge intentionally removed — parents shouldn't see internal lead/member tagging. */}
                        </div>
                    </div>
                </div>
            </section>

            {!effectiveProfileComplete ? (
                <section className="py-12 md:py-16">
                    <div className="max-w-4xl mx-auto px-6 md:px-10">
                        <ProfileWizard
                            initialName={user.name}
                            initialEmail={user.email}
                            onComplete={async () => {
                                await refreshUser();
                            }}
                        />
                    </div>
                </section>
            ) : (
                <ProfileSummary user={user} refreshUser={refreshUser} onEdit={() => navigate("/dashboard?edit=1")} />
            )}

            {effectiveProfileComplete && (
                <section className="border-t border-zinc-900 py-8 md:py-10">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-6">
                        <div data-testid="quick-links-row" className="flex flex-wrap items-center gap-2">
                            {pricesGate.visible ? (
                                <Link
                                    to="/policy/membership"
                                    data-testid="current-prices-btn"
                                    className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase px-3 py-2 border border-brand-red text-brand-red hover:bg-brand-red hover:text-white transition-colors rounded"
                                >
                                    <Tag className="w-3.5 h-3.5" />
                                    Current prices
                                </Link>
                            ) : (
                                <button
                                    type="button"
                                    disabled
                                    data-testid="current-prices-btn-locked"
                                    title="Prices unlock 15 minutes into your assessment"
                                    className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase px-3 py-2 border border-zinc-800 text-zinc-600 rounded cursor-not-allowed"
                                >
                                    <Tag className="w-3.5 h-3.5" />
                                    Current prices · locked
                                </button>
                            )}
                            <span className="text-[11px] text-zinc-500 ml-1">
                                {pricesGate.visible
                                    ? "See plans, packs, and what you'd pay before your coach charges anything."
                                    : "Prices unlock 15 minutes into your assessment."}
                            </span>
                        </div>
                        <FirstStepsCard />
                        <DashboardSummaryStrip />
                        <MemberAlertsStrip onChange={refreshUser} />
                        <div className="grid lg:grid-cols-2 gap-6">
                            <LatestSessionLiteCard />
                            <UpcomingSessionsCard />
                        </div>
                    </div>
                </section>
            )}

            {effectiveProfileComplete && !user.self_waiver_signed && !user.waiver?.signed_at && !user.waiver_signed_at && (
                <section data-testid="sign-waiver-cta" className="border-t border-zinc-900 bg-amber-500/5">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <FileSignature className="w-5 h-5 text-amber-400" />
                            <div>
                                <div className="text-sm font-semibold text-white">Waiver still pending</div>
                                <div className="text-xs text-zinc-200">Sign the participant agreement before your assessment.</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="open-sign-waiver"
                            onClick={() => setWaiverOpen(true)}
                            className="btn-tactical text-xs"
                        >
                            <FileSignature className="w-3.5 h-3.5" /> Sign waiver
                        </button>
                    </div>
                </section>
            )}

            <section className="py-14 md:py-20 border-t border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid md:grid-cols-12 gap-10 items-end mb-8">
                        <div className="md:col-span-7">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                                <span className="inline-block w-8 h-px bg-brand-red" />
                                Coming next
                            </div>
                            <h2 className="font-display text-3xl md:text-5xl tracking-wider text-white leading-[0.95]">
                                THE MEMBER AREA, BUILT NEXT.
                            </h2>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {ROADMAP.filter((t) => {
                            // Hide Workouts tile unless the parent is themselves an athlete.
                            if (t.route === "/dashboard/workouts") {
                                return (user?.athletes || []).length === 1 && Boolean(user.athletes[0]?.is_self_athlete);
                            }
                            // Hide Pricing tile entirely for leads — Sachin
                            // 2026-03-05: "The only way to see prices should
                            // be when I send manually." Members/staff still
                            // see it (they already have skin in the game).
                            if (t.route === "/dashboard/pricing") {
                                return pricesGate.visible;
                            }
                            return true;
                        }).map((r, i) => {
                            const Card = r.route ? Link : "div";
                            const cardProps = r.route ? { to: r.route } : {};
                            // Alternate accent color: red, navy, red, navy ...
                            const useNavy = i % 2 === 1;
                            const hoverBorder = r.route ? (useNavy ? "hover:border-brand-navy-500" : "hover:border-brand-red") : "";
                            const accentText = useNavy ? "text-brand-navy-300" : "text-brand-red";
                            const iconBox = useNavy
                                ? "text-brand-navy-300 group-hover:bg-brand-navy-700 group-hover:border-brand-navy-700"
                                : "text-brand-red group-hover:bg-brand-red group-hover:border-brand-red";
                            return (
                                <Card
                                    key={r.title}
                                    {...cardProps}
                                    data-testid={`dashboard-roadmap-${i}`}
                                    className={`p-6 border border-zinc-900 bg-zinc-950 ${r.route ? `${hoverBorder} transition-colors group` : ""}`}
                                >
                                    <div className={`inline-flex items-center justify-center w-10 h-10 bg-black border border-zinc-800 mb-4 ${iconBox} group-hover:text-white transition-all`}>
                                        <r.icon className="w-4 h-4" />
                                    </div>
                                    <div className="font-display text-2xl tracking-wider text-white">
                                        {r.title}
                                    </div>
                                    <div className={`mt-2 text-[10px] tracking-[0.25em] uppercase ${r.route ? accentText : "text-zinc-500"}`}>
                                        {r.route ? "Open →" : "Coming soon"}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <ActivePacksPanel parentView />
                        <ThisWeekSchedule />
                    </div>
                    <div className="mt-10">
                        <Link to="/programs/school" data-testid="dashboard-cta-programs" className="btn-tactical inline-flex">
                            Browse programs <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <SignWaiverModal
                open={waiverOpen}
                onClose={() => setWaiverOpen(false)}
                onDone={async () => { setWaiverOpen(false); await refreshUser(); }}
            />
        </div>
    );
}

function ProfileSummary({ user, refreshUser }) {
    const addr = user.address || {};
    const addrLine =
        [addr.line1, addr.line2, [addr.city, addr.state].filter(Boolean).join(", "), addr.postal_code]
            .filter(Boolean)
            .join(" · ") || "—";
    const [editor, setEditor] = useState(null); // null | {mode:"add"} | {mode:"edit", athlete}

    return (
        <section className="py-12 md:py-16" data-testid="dashboard-profile-summary">
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-6">
                {/* Account holder card */}
                <div className="md:col-span-5 border border-zinc-900 bg-zinc-950 p-6 md:p-8">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                        <UserCircle2 className="w-3.5 h-3.5" /> Account holder
                    </div>
                    <div className="font-display text-3xl md:text-4xl tracking-wider text-white leading-tight">
                        {user.parent_first_name || user.name} {user.parent_last_name || ""}
                    </div>
                    <ul className="mt-5 space-y-2 text-sm text-zinc-300">
                        <li className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-brand-red" /> {user.email}
                        </li>
                        {user.phone && (
                            <li className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-brand-red" /> {user.phone}
                            </li>
                        )}
                        <li className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-brand-red mt-0.5" />
                            <span className="text-zinc-200">{addrLine}</span>
                        </li>
                    </ul>
                    {user.emergency_contact && (
                        <div className="mt-6 pt-6 border-t border-zinc-900">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-2">
                                Emergency contact
                            </div>
                            <div className="text-sm text-white">
                                {user.emergency_contact.name}{" "}
                                <span className="text-zinc-300">· {user.emergency_contact.relationship}</span>
                            </div>
                            <div className="text-sm text-zinc-200">{user.emergency_contact.phone}</div>
                        </div>
                    )}
                    {user.waiver && (
                        <div className="mt-6 pt-6 border-t border-zinc-900 flex items-start gap-3">
                            <FileSignature className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-zinc-200">
                                Waiver {user.waiver.version} signed by{" "}
                                <span className="text-white">{user.waiver.signed_name}</span>
                                {user.waiver.signed_at && (
                                    <> on {new Date(user.waiver.signed_at).toLocaleDateString()}</>
                                )}.
                            </div>
                        </div>
                    )}
                </div>

                {/* Athletes */}
                <div className="md:col-span-7">
                    <AthleteSubscriptionPanel />
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                        <span className="inline-block w-8 h-px bg-brand-red" /> Athletes
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {(user.athletes || []).map((a) => {
                            const age = formatAge(a.dob);
                            return (
                                <div
                                    key={a.athlete_id}
                                    data-testid={`dashboard-athlete-${a.athlete_id}`}
                                    className="relative border border-zinc-900 bg-zinc-950 p-5 hover:border-brand-red transition-colors"
                                >
                                    <button
                                        type="button"
                                        onClick={() => setEditor({ mode: "edit", athlete: a })}
                                        data-testid={`dashboard-athlete-edit-${a.athlete_id}`}
                                        className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-zinc-300 hover:text-brand-red transition-colors"
                                        aria-label="Edit athlete"
                                    >
                                        <Pencil className="w-3 h-3" /> Edit
                                    </button>
                                    <div className="font-display text-2xl tracking-wider text-white pr-12">
                                        {a.first_name} {a.last_name}
                                    </div>
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mt-1">
                                        {age != null ? `Age ${age}` : "—"} · {TRACK_LABELS[a.track] || a.track}
                                    </div>
                                    {a.health_notes && (
                                        <p className="text-xs text-zinc-200 mt-3 leading-relaxed">
                                            {a.health_notes}
                                        </p>
                                    )}
                                    <AthleteModeManager athlete={a} onChange={refreshUser} />
                                </div>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => setEditor({ mode: "add" })}
                            data-testid="dashboard-athlete-add"
                            className="border border-dashed border-zinc-700 hover:border-brand-red bg-zinc-950/40 p-5 flex flex-col items-center justify-center text-center text-zinc-300 hover:text-brand-red transition-colors min-h-[140px] gap-2"
                        >
                            <Plus className="w-5 h-5" />
                            <span className="text-[11px] tracking-[0.25em] uppercase font-semibold">
                                Add another athlete
                            </span>
                            <span className="text-[10px] text-zinc-500">
                                Add a kid you didn&apos;t list at signup
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            {editor && (
                <AthleteEditorModal
                    mode={editor.mode}
                    athlete={editor.athlete}
                    onClose={() => setEditor(null)}
                    onSaved={refreshUser}
                />
            )}
        </section>
    );
}

function SignWaiverModal({ open, onClose, onDone }) {
    const [name, setName] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    if (!open) return null;

    const submit = async () => {
        if (!name.trim()) { toast.error("Please type your full name"); return; }
        if (!agreed) { toast.error("You must confirm you've read and agree to the waiver"); return; }
        setSubmitting(true);
        try {
            await axios.patch(`${API}/profile/waiver`, {
                signed_name: name.trim(),
                version: WAIVER_VERSION,
            }, { withCredentials: true });
            toast.success("Waiver signed — thank you");
            onDone();
        } catch (e) {
            toast.error("Couldn't sign", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="sign-waiver-modal" className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl my-8">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Participant Agreement</h3>
                    <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="max-h-72 overflow-y-auto bg-black border border-zinc-900 rounded-lg p-4 text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap font-mono">
                        {WAIVER_TEXT}
                    </div>
                    <label className="flex items-start gap-2 text-sm text-zinc-300">
                        <input
                            type="checkbox"
                            data-testid="waiver-modal-agree"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            className="mt-0.5"
                        />
                        <span>I have read and agree to the participant agreement above.</span>
                    </label>
                    <label className="block">
                        <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-300">Type your full legal name to sign</span>
                        <input
                            data-testid="waiver-modal-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                        />
                    </label>
                    <p className="text-[11px] text-zinc-300">Waiver version {WAIVER_VERSION}</p>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-ghost-tactical" disabled={submitting}>Cancel</button>
                        <button
                            type="button"
                            data-testid="waiver-modal-submit"
                            onClick={submit}
                            disabled={submitting}
                            className="btn-tactical"
                        >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            Sign waiver
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

